import React, { lazy } from "react";
import { Navigate, Outlet } from "react-router-dom";
import DonutChartTest from "../components/Charts/__tests__/manual-test";

// Lazy load all components
const Integrations = lazy(() => import("../views/Integrations/Integrations"));
const Analytics = lazy(() => import("../views/Analytics/Analytics"));
const Dashboard = lazy(() => import("../views/Dashboard"));
const ConfigureMonitoring = lazy(() =>
  import("../views/Monitoring/ConfigureMonitoring")
);
const SetupProfile = lazy(() => import("../views/OnBoarding/SetupProfile"));
const BuildLogs = lazy(() => import("../views/Projects/BuildLogs"));
const ConfigureAccount = lazy(() =>
  import("../views/Projects/ConfigureAccount")
);
const CustomDomain = lazy(() => import("../views/Projects/CustomDomain"));
const Projects = lazy(() => import("../views/Projects/Projects"));
const ProjectsDashboard = lazy(() => import("../views/Projects"));
const ProjectSettings = lazy(() => import("../views/Projects/ProjectSettings"));
const SetupProject = lazy(() => import("../views/Projects/SetupProject"));
const Teams = lazy(() => import("../views/Teams/Teams"));
const PandaEdge = lazy(() => import("../views/PandaEdge"));
const PandaEdgePanel = lazy(() => import("../views/PandaEdge/PandaEdgePanel"));
const CreatePandaEdge = lazy(() =>
  import("../views/PandaEdge/CreatePandaEdge")
);
const Monitoring = lazy(() => import("../views/Monitoring"));
const MonitoringDetails = lazy(() =>
  import("../views/Monitoring/MonitoringDetails")
);
const ManageAnalytics = lazy(() => import("../views/Analytics"));
const SSO = lazy(() => import("../views/Sso/SsoDashboard"));
const SsoSelection = lazy(() => import("../views/Sso/SsoSelection"));
const SsoConfigure = lazy(() => import("../views/Sso/SsoConfigure"));
const SsoList = lazy(() => import("../views/Sso"));
const Settings = lazy(() => import("../views/Settings/Settings"));
const ActivityLogs = lazy(() => import("../views/ActivityLogs/ActivityLogs"));
const ManagedAppsDashboard = lazy(() => import("../views/ManagedApps"));
const DeployManagedApp = lazy(() =>
  import("../views/ManagedApps/DeployManagedApp")
);
const ServerConfiguration = lazy(() =>
  import("../views/ManagedApps/ServerConfiguration")
);
const ManagedAppSettings = lazy(() =>
  import("../views/ManagedApps/ManagedAppSettings")
);
const AI = lazy(() => import("../views/AI"));
const CliAuth = lazy(() => import("../views/CliAuth/CliAuth"));

// Database Components
const Database = lazy(() => import("../views/database/Database"));
const DatabaseDetails = lazy(() => import("../views/database/DatabaseDetails"));
const CreateDatabase = lazy(() => import("../views/database/createDatabase"));
const SelectDatabase = lazy(() => import("../views/database/SelectDatabase"));
const DeployingLogsConfigure = lazy(() =>
  import("../views/database/DeployingLogsConfigure")
);
const ConfigureDatabase = lazy(() =>
  import("../views/database/ConfigureDatabase")
);
// Edge Functions Components
const EdgeFunctions = lazy(() => import("../views/EdgeFunctions"));
const CreateEdgeFunction = lazy(() =>
  import("../views/EdgeFunctions/CreateEdgeFunction")
);
const EditEdgeFunction = lazy(() =>
  import("../views/EdgeFunctions/EditEdgeFunction")
);
const EdgeFunctionDetails = lazy(() =>
  import("../views/EdgeFunctions/EdgeFunctionDetails")
);
const EdgeFunctionMonitoring = lazy(() =>
  import("../views/EdgeFunctions/EdgeFunctionMonitoring")
);
const EdgeFunctionLogs = lazy(() =>
  import("../views/EdgeFunctions/EdgeFunctionLogs")
);
const EdgeFunctionTesting = lazy(() =>
  import("../views/EdgeFunctions/EdgeFunctionTesting")
);
const EdgeFunctionSettings = lazy(() =>
  import("../views/EdgeFunctions/EdgeFunctionSettings")
);

// Protected Routes Component
export const ProtectedRoutes = () => {
  const token = localStorage.getItem("authToken");
  return token ? <Outlet /> : <Navigate to="/login" />;
};

// Private Routes Configuration
export const PrivateRoutes = [
  {
    path: "/cli-auth",
    name: "CLI Authentication",
    element: <CliAuth />,
    hideFromNav: true,
    hideLayout: true,
  },
  {
    path: "/chart-test",
    name: "Chart Test",
    element: <DonutChartTest />,
    hideFromNav: true,
  },
  {
    path: "/",
    name: "Dashboard",
    element: <Dashboard />,
    icon: "pi pi-home",
  },
  {
    path: "/projects",
    name: "Projects",
    element: <ProjectsDashboard />,
    icon: "pi pi-sitemap",
  },
  {
    path: "/projects/:name",
    name: "Project Details",
    element: <Projects />,
    icon: "pi pi-sitemap",
    hideFromNav: true,
  },
  {
    path: "/projects/configure-account",
    name: "Configure Account",
    element: <ConfigureAccount />,
    icon: "pi pi-sitemap",
    hideFromNav: true,
  },
  {
    path: "/projects/setup-project",
    name: "Setup Project",
    element: <SetupProject />,
    icon: "pi pi-sitemap",
    hideFromNav: true,
  },
  {
    path: "/project/:name/build-logs",
    name: "Build Logs",
    element: <BuildLogs />,
    icon: "pi pi-sitemap",
    hideFromNav: true,
  },
  {
    path: "/project/:name/custom-domain",
    name: "Custom Domain",
    element: <CustomDomain />,
    icon: "pi pi-sitemap",
    hideFromNav: true,
  },
  {
    path: "/project/:name/settings",
    name: "Project Settings",
    element: <ProjectSettings />,
    icon: "pi pi-sitemap",
    hideFromNav: true,
  },
  {
    path: "/teams",
    name: "Teams",
    element: <Teams />,
    icon: "pi pi-user-plus",
  },
  {
    path: "/monitoring",
    name: "Monitoring",
    element: <Monitoring />,
    icon: "pi pi-chart-bar",
  },
  {
    path: "/monitoring/:name",
    name: "Monitoring",
    element: <MonitoringDetails />,
    icon: "pi pi-chart-bar",
    hideFromNav: true,
  },
  {
    path: "/monitoring/add-new",
    name: "Monitoring",
    element: <ConfigureMonitoring />,
    icon: "pi pi-chart-bar",
    hideFromNav: true,
  },
  {
    path: "/monitoring/settings/:name",
    name: "Monitoring",
    element: <ConfigureMonitoring />,
    icon: "pi pi-chart-bar",
    hideFromNav: true,
  },
  {
    path: "/analytics",
    name: "Analytics",
    element: <ManageAnalytics />,
    icon: "pi pi-chart-line",
  },
  {
    path: "/analytics/:name",
    name: "Analytics",
    element: <Analytics />,
    icon: "pi pi-chart-line",
    hideFromNav: true,
  },
  {
    path: "/managed-apps",
    name: "Managed Apps",
    element: <ManagedAppsDashboard />,
    icon: "pi pi-thumbtack",
  },
  {
    path: "/managed-apps/deploy",
    name: "Deploy Managed App",
    element: <DeployManagedApp />,
    icon: "pi pi-thumbtack",
    hideFromNav: true,
  },
  {
    path: "/managed-apps/configure",
    name: "Configure Server",
    element: <ServerConfiguration />,
    icon: "pi pi-thumbtack",
    hideFromNav: true,
  },
  {
    path: "/managed-apps/:name/settings",
    name: "Managed App Settings",
    element: <ManagedAppSettings />,
    icon: "pi pi-thumbtack",
    hideFromNav: true,
  },
  {
    path: "/ai",
    name: "AI",
    element: <AI />,
    icon: "pi pi-microchip-ai",
  },
  // Database Routes
  {
    path: "/database",
    name: "Database",
    element: <Database />,
    icon: "pi pi-database",
  },
  {
    path: "/database/select-database",
    name: "Select Database",
    element: <SelectDatabase />,
    hideFromNav: true,
  },
  {
    path: "/database/configure-database",
    name: "Configure Database",
    element: <CreateDatabase />,
    hideFromNav: true,
  },
  {
    path: "/database/:name",
    name: "Database Details",
    element: <DatabaseDetails />,
    hideFromNav: true,
  },
  {
    path: "/database/:name/monitoring",
    name: "Database Monitoring",
    element: <DatabaseDetails />,
    hideFromNav: true,
  },
  {
    path: "/database/:name/topology",
    name: "Database Topology",
    element: <DatabaseDetails />,
    hideFromNav: true,
  },
  {
    path: "/database/:name/backup",
    name: "Database Backup",
    element: <DatabaseDetails />,
    hideFromNav: true,
  },
  {
    path: "/database/:name/security",
    name: "Database Security",
    element: <DatabaseDetails />,
    hideFromNav: true,
  },
  {
    path: "/database/:name/configure-database",
    name: "Configure Database",
    element: <DatabaseDetails />,
    hideFromNav: true,
  },
  {
    path: "/database/deploying-logs",
    name: "Deploying Logs",
    element: <DeployingLogsConfigure />,
    hideFromNav: true,
  },
  // Edge Functions Routes
  {
    path: "/edge",
    name: "PandaEdge",
    element: <EdgeFunctions />,
    icon: "pi pi-bolt",
  },
  {
    path: "/edge/create",
    name: "Create Edge Function",
    element: <CreateEdgeFunction />,
    hideFromNav: true,
  },
  {
    path: "/edge/:id",
    name: "Edge Function Details",
    element: <EdgeFunctionDetails />,
    hideFromNav: true,
  },
  {
    path: "/edge/:id/edit",
    name: "Edit Edge Function",
    element: <EditEdgeFunction />,
    hideFromNav: true,
  },
  {
    path: "/edge/:id/monitoring",
    name: "Edge Function Monitoring",
    element: <EdgeFunctionMonitoring />,
    hideFromNav: true,
  },
  {
    path: "/edge/:id/logs",
    name: "Edge Function Logs",
    element: <EdgeFunctionLogs />,
    hideFromNav: true,
  },
  {
    path: "/edge/:id/testing",
    name: "Edge Function Testing",
    element: <EdgeFunctionTesting />,
    hideFromNav: true,
  },
  {
    path: "/edge/:id/settings",
    name: "Edge Function Settings",
    element: <EdgeFunctionSettings />,
    hideFromNav: true,
  },
  // SSO Routes
  {
    path: "/sso",
    name: "SSO",
    element: <SSO />,
    icon: "pi pi-shield",
    hideFromNav: true,
  },
  {
    path: "/sso/select",
    name: "SSO",
    element: <SsoSelection />,
    icon: "pi pi-shield",
    hideFromNav: true,
  },
  {
    path: "/sso/list",
    name: "SSO",
    element: <SsoList />,
    icon: "pi pi-shield",
  },
  {
    path: "/sso/configure",
    name: "SSO",
    element: <SsoConfigure />,
    icon: "pi pi-shield",
    hideFromNav: true,
  },
  // Other Routes
  {
    path: "/integrations",
    name: "Integrations",
    element: <Integrations />,
    icon: "pi pi-sliders-h",
  },
  {
    path: "/activity-logs",
    name: "Activity Logs",
    element: <ActivityLogs />,
    icon: "pi pi-receipt",
  },
  {
    path: "/settings",
    name: "Settings",
    element: <Settings />,
    icon: "pi pi-cog",
  },
];
